import { useCallback, useRef } from 'react';

interface ResizeHandleProps {
  direction: 'vertical' | 'horizontal';
  onResize: (delta: number) => void;
  onResizeEnd?: () => void;
}

export function ResizeHandle({ direction, onResize, onResizeEnd }: ResizeHandleProps) {
  const dragging = useRef(false);
  const lastPos = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    lastPos.current = direction === 'vertical' ? e.clientX : e.clientY;

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      const current = direction === 'vertical' ? ev.clientX : ev.clientY;
      const delta = current - lastPos.current;
      lastPos.current = current;
      onResize(delta);
    };

    const handleMouseUp = () => {
      dragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onResizeEnd?.();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = direction === 'vertical' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
  }, [direction, onResize, onResizeEnd]);

  const isVertical = direction === 'vertical';

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`
        relative flex-shrink-0 z-10 group
        ${isVertical ? 'w-[8px] cursor-col-resize' : 'h-[8px] cursor-row-resize'}
      `}
    >
      {/* Visible line in the center */}
      <div
        className={`
          absolute bg-[var(--border-subtle)] group-hover:bg-[rgba(74,127,255,0.3)] group-active:bg-[rgba(74,127,255,0.5)]
          transition-colors duration-150
          ${isVertical ? 'w-[1px] inset-y-0 left-1/2 -translate-x-1/2' : 'h-[1px] inset-x-0 top-1/2 -translate-y-1/2'}
        `}
      />
    </div>
  );
}
